import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const smoothScrollTo = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const navigationItems = [
    { name: 'About', id: 'about' },
    { name: 'Philosophy', id: 'philosophy' },
    { name: 'Directors', id: 'artistic-directors' },
    { name: 'Services', id: 'experience-tiers' },
    { name: 'Areas', id: 'service-areas' },
    { name: 'Contact', id: 'booking' },
  ];

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img 
          src="https://public.readdy.ai/ai/img_res/3e794506a9bbcd357e6f66d622344a73.jpg" 
          alt="Grand Salon Interior"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <h1 className={`font-serif text-lg sm:text-xl lg:text-2xl font-bold transition-colors duration-300 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                The Salon Society
              </h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              {navigationItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => smoothScrollTo(item.id)}
                  className={`relative text-sm font-medium transition-colors duration-300 group whitespace-nowrap ${
                    isScrolled ? 'text-gray-700 hover:text-amber-600' : 'text-white hover:text-amber-200'
                  }`}
                >
                  {item.name}
                  <span className={`absolute left-0 bottom-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${
                    isScrolled ? 'bg-amber-600' : 'bg-amber-200'
                  }`}></span>
                </button>
              ))}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-md transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700 hover:text-amber-600' : 'text-white hover:text-amber-200'
                }`}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navigationItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => smoothScrollTo(item.id)}
                    className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-700 hover:text-amber-600 hover:bg-amber-50 transition-colors duration-300 rounded-md"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
        <div className="max-w-2xl">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white mb-4 sm:mb-6 leading-tight">
            The Salon Society
          </h1>
          <p className="text-lg sm:text-xl lg:text-2xl xl:text-3xl text-white/90 font-light mb-3 sm:mb-4">
            Curated classical concerts in the comfort of your home.
          </p>
          <p className="text-base sm:text-lg lg:text-xl text-white/80 italic mb-4 sm:mb-6">
            Private. Refined. Unforgettable.
          </p>
          <p className="text-sm sm:text-base lg:text-lg text-white/85 mb-6 sm:mb-8 lg:mb-10 leading-relaxed max-w-xl">
            Bespoke chamber music experiences serving patrons in Nashville, Brentwood, Franklin, and Green Hills.
          </p>
          
          {/* Call to Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
            <button
              onClick={() => smoothScrollTo('booking')}
              className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-amber-600 hover:bg-amber-700 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full text-sm sm:text-base lg:text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <span>Request an Invitation</span>
              <span className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-full">
                <i className="ri-arrow-right-line text-amber-600 text-sm sm:text-lg"></i>
              </span>
            </button>

            <button
              onClick={() => smoothScrollTo('booking')}
              className="inline-flex items-center justify-center gap-2 sm:gap-3 border-2 border-white/80 hover:border-white hover:bg-white/10 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full text-sm sm:text-base lg:text-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8">
                <i className="ri-calendar-check-line text-white text-sm sm:text-xl"></i>
              </span>
              <span>Book Now</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Quote */}
      <div className="absolute bottom-12 sm:bottom-16 lg:bottom-20 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <p className="font-serif text-base sm:text-lg lg:text-2xl xl:text-3xl text-white/70 italic max-w-4xl leading-relaxed">
            "For centuries, Europe's cultural elite gathered in private salons for intimate performances of the highest artistic caliber..."
          </p>
        </div>
      </div>
    </section>
  );
}
